// Financial Reporting Service - Recovery, Operations, Projections
import { apiGet, API_BASE, getMultipartAuthHeaders } from '@/utils/api';

// ==================== Types ====================

export interface RecoveryRateByFiliere {
  filiereId: string;
  filiereName: string;
  filiereCode: string;
  totalDue: number;
  totalPaid: number;
  recoveryRate: number;
  studentCount: number;
}

export interface RecoveryRateByLevel {
  level: number;
  totalDue: number;
  totalPaid: number;
  recoveryRate: number;
  studentCount: number;
}

export interface RecoveryDashboardResponse {
  globalRecoveryRate: number;
  totalDue: number;
  totalPaid: number;
  totalStudents: number;
  paidOnTime: number;
  onTimeRate: number;
  byFiliere: RecoveryRateByFiliere[];
  byLevel: RecoveryRateByLevel[];
}

export interface PaymentMethodDistribution {
  method: 'CASH' | 'BANK_TRANSFER' | 'CHECK';
  methodLabel: string;
  amount: number;
  count: number;
  percentage: number;
}

export interface CollectionSummaryResponse {
  totalCollected: number;
  paymentCount: number;
  distribution: PaymentMethodDistribution[];
  periodStart: string;
  periodEnd: string;
}

export type AgingBucket = '15-30' | '31-60' | '61-90' | '90+';

export interface OverdueStudent {
  id: string;
  userId: string;
  studentCode: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  filiereId: string;
  filiereName: string;
  filiereCode: string;
  level: number;
  amountDue: number;
  daysOverdue: number;
  agingBucket: AgingBucket;
  dueDate: string;
  gracePeriodDays: number;
}

export interface OverdueStudentsResponse {
  students: OverdueStudent[];
  totalOverdue: number;
  totalAmountDue: number;
  byBucket: {
    '15-30': { count: number; amount: number };
    '31-60': { count: number; amount: number };
    '61-90': { count: number; amount: number };
    '90+': { count: number; amount: number };
  };
}

export interface OperationsReportResponse {
  permanentTotal: number;
  permanentCount: number;
  vacataireTotal: number;
  vacataireCount: number;
  combinedTotal: number;
  period: {
    month?: number;
    year: number;
    startDate?: string;
    endDate?: string;
  };
  details: {
    id: string;
    type: 'permanent' | 'vacataire';
    name: string;
    department: string;
    hoursTaught: number;
    netPaid: number;
  }[];
}

export interface TeachingHoursResponse {
  totalHours: number;
  permanentHours: number;
  vacataireHours: number;
  permanentPercentage: number;
  vacatairePercentage: number;
  period: {
    month?: number;
    year: number;
    startDate?: string;
    endDate?: string;
  };
}

export interface VacataireMonthBreakdown {
  name: string;
  subject: string;
  hours: number;
  hourlyRate: number;
  cost: number;
}

export interface MonthlyProjection {
  month: number;
  year: number;
  monthName: string;
  permanentCost: number;
  vacataireCost: number;
  vacataireHours: number;
  totalCost: number;
  isPast: boolean;
  vacataireBreakdown: VacataireMonthBreakdown[];
}

export interface VacataireProjectionDetail {
  userId: string;
  name: string;
  subject: string;
  hoursRemaining: number;
  hourlyRate: number;
  projectedCost: number;
}

export interface FinancialProjectionsResponse {
  currentMonthlySalaryMass: number;
  projectedAnnualCost: number;
  projectionMonths: number;
  monthlyProjections: MonthlyProjection[];
  vacataireDetails: VacataireProjectionDetail[];
}

export interface ReportFilters {
  academicYearId: string;
  campusId?: string;
  month?: number;
  year?: number;
  startDate?: string;
  endDate?: string;
  periodType?: 'monthly' | 'yearly' | 'custom';
}

// ==================== Helpers ====================

function buildQueryParams(filters: ReportFilters): URLSearchParams {
  const params = new URLSearchParams();
  params.set('academicYearId', filters.academicYearId);
  if (filters.campusId) params.set('campusId', filters.campusId);
  if (filters.periodType) params.set('periodType', filters.periodType);
  if (filters.month !== undefined) params.set('month', String(filters.month));
  if (filters.year !== undefined) params.set('year', String(filters.year));
  if (filters.startDate) params.set('startDate', filters.startDate);
  if (filters.endDate) params.set('endDate', filters.endDate);
  return params;
}

// ==================== Recovery Report APIs ====================

/** Campuses the current user may filter reports by (scoped to their assignment). */
export async function getReportFilterCampuses(): Promise<{ campuses: { id: string; name: string }[]; scoped: boolean }> {
  return apiGet<{ campuses: { id: string; name: string }[]; scoped: boolean }>(
    `/financial/reports/filter-campuses`,
  );
}

export async function getRecoveryDashboard(filters: ReportFilters): Promise<RecoveryDashboardResponse> {
  const params = buildQueryParams(filters);
  return apiGet<RecoveryDashboardResponse>(`/financial/reports/recovery-dashboard?${params}`);
}

export async function getCollectionSummary(filters: ReportFilters): Promise<CollectionSummaryResponse> {
  const params = buildQueryParams(filters);
  return apiGet<CollectionSummaryResponse>(`/financial/reports/collection-summary?${params}`);
}

export async function getOverdueStudents(filters: ReportFilters): Promise<OverdueStudentsResponse> {
  const params = buildQueryParams(filters);
  return apiGet<OverdueStudentsResponse>(`/financial/reports/overdue-students?${params}`);
}

// ==================== Operations Report APIs ====================

export async function getOperationsReport(filters: ReportFilters): Promise<OperationsReportResponse> {
  const params = buildQueryParams(filters);
  return apiGet<OperationsReportResponse>(`/financial/reports/operations?${params}`);
}

export async function getTeachingHoursDistribution(filters: ReportFilters): Promise<TeachingHoursResponse> {
  const params = buildQueryParams(filters);
  return apiGet<TeachingHoursResponse>(`/financial/reports/teaching-hours?${params}`);
}

// ==================== Projections Report APIs ====================

export async function getFinancialProjections(
  filters: ReportFilters,
): Promise<FinancialProjectionsResponse> {
  const params = buildQueryParams(filters);
  return apiGet<FinancialProjectionsResponse>(`/financial/reports/projections?${params}`);
}

// ==================== Export APIs ====================

export async function exportRecoveryReport(filters: ReportFilters): Promise<Blob> {
  const params = buildQueryParams(filters);
  const response = await fetch(
    `${API_BASE}/financial/reports/export/recovery?${params}`,
    { headers: getMultipartAuthHeaders() },
  );
  if (!response.ok) {
    throw new Error('Erreur lors de l\'export du rapport de recouvrement');
  }
  return response.blob();
}

export async function exportOperationsReport(filters: ReportFilters): Promise<Blob> {
  const params = buildQueryParams(filters);
  const response = await fetch(
    `${API_BASE}/financial/reports/export/operations?${params}`,
    { headers: getMultipartAuthHeaders() },
  );
  if (!response.ok) {
    throw new Error('Erreur lors de l\'export du rapport des operations');
  }
  return response.blob();
}

export async function exportProjectionsReport(filters: ReportFilters): Promise<Blob> {
  const params = buildQueryParams(filters);
  const response = await fetch(
    `${API_BASE}/financial/reports/export/projections?${params}`,
    { headers: getMultipartAuthHeaders() },
  );
  if (!response.ok) {
    throw new Error('Erreur lors de l\'export des projections');
  }
  return response.blob();
}

// ==================== PDF Export APIs ====================

export async function exportRecoveryReportPdf(filters: ReportFilters): Promise<Blob> {
  const params = buildQueryParams(filters);
  const response = await fetch(
    `${API_BASE}/financial/reports/export/recovery/pdf?${params}`,
    { headers: getMultipartAuthHeaders() },
  );
  if (!response.ok) {
    throw new Error('Erreur lors de l\'export PDF du rapport de recouvrement');
  }
  return response.blob();
}

export async function exportOperationsReportPdf(filters: ReportFilters): Promise<Blob> {
  const params = buildQueryParams(filters);
  const response = await fetch(
    `${API_BASE}/financial/reports/export/operations/pdf?${params}`,
    { headers: getMultipartAuthHeaders() },
  );
  if (!response.ok) {
    throw new Error('Erreur lors de l\'export PDF du rapport des operations');
  }
  return response.blob();
}

export async function exportProjectionsReportPdf(filters: ReportFilters): Promise<Blob> {
  const params = buildQueryParams(filters);
  const response = await fetch(
    `${API_BASE}/financial/reports/export/projections/pdf?${params}`,
    { headers: getMultipartAuthHeaders() },
  );
  if (!response.ok) {
    throw new Error('Erreur lors de l\'export PDF des projections');
  }
  return response.blob();
}

// ==================== Download Helper ====================

export function downloadBlob(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

// ==================== Formatting Helpers ====================

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('fr-MA', {
    style: 'currency',
    currency: 'MAD',
    minimumFractionDigits: 0,
  }).format(amount);
}

export function formatPercentage(value: number): string {
  return `${value.toFixed(1)}%`;
}

export function getAgingBucketColor(bucket: AgingBucket): string {
  switch (bucket) {
    case '15-30':
      return 'bg-yellow-100 text-yellow-800';
    case '31-60':
      return 'bg-orange-100 text-orange-800';
    case '61-90':
      return 'bg-red-100 text-red-800';
    case '90+':
      return 'bg-red-200 text-red-900';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

export function getPaymentMethodLabel(method: string): string {
  const labels: Record<string, string> = {
    CASH: 'Especes',
    BANK_TRANSFER: 'Virement',
    CHECK: 'Cheque',
  };
  return labels[method] || method;
}

export const MONTHS = [
  { value: 1, label: 'Janvier' },
  { value: 2, label: 'Fevrier' },
  { value: 3, label: 'Mars' },
  { value: 4, label: 'Avril' },
  { value: 5, label: 'Mai' },
  { value: 6, label: 'Juin' },
  { value: 7, label: 'Juillet' },
  { value: 8, label: 'Aout' },
  { value: 9, label: 'Septembre' },
  { value: 10, label: 'Octobre' },
  { value: 11, label: 'Novembre' },
  { value: 12, label: 'Decembre' },
];
